import * as React from 'react'
import { createPortal } from 'react-dom'
import { Loader2 } from 'lucide-react'
import { FieldControl } from './FieldOverlay'
import { DraftDot, QuestionDot } from './QuestionDot'
import type { FieldDto, FieldValueDto } from '@lib/dto/envelope.dto'
import type { QuestionLayer } from './QuestionSidebar'
import type { Uuid } from '@lib/validators/params'
import { usePaper } from '@/theme/ThemeProvider'

const ASSET_RE = /pixelsign-asset:([0-9a-f-]{36})/gi

export function HtmlDocumentViewer({
  documentId,
  fields = [],
  values = {},
  viewerSignerId = '',
  signedSignerIds = [],
  onChange,
  onClickSign,
  questions,
  watermark = false,
  loadDocument,
  loadAsset,
}: {
  documentId: Uuid
  fields?: readonly FieldDto[]
  values?: Record<string, FieldValueDto>
  viewerSignerId?: string
  signedSignerIds?: readonly string[]
  onChange?: ((field: FieldDto, value: FieldValueDto) => void) | undefined
  onClickSign?: ((field: FieldDto) => void) | undefined
  questions?: QuestionLayer | undefined
  /** Marks the document TEST down its length (see PacketViewer). */
  watermark?: boolean
  loadDocument: (documentId: string) => Promise<Blob>
  loadAsset: (documentId: string, assetId: string) => Promise<Blob>
}) {
  const hostRef = React.useRef<HTMLDivElement>(null)
  const [html, setHtml] = React.useState<string | null>(null)
  const [mounts, setMounts] = React.useState<Map<string, Element>>(() => new Map())
  const blobUrlsRef = React.useRef<string[]>([])
  const paper = usePaper()

  React.useEffect(() => {
    let cancelled = false
    const urls: string[] = []
    async function load(): Promise<void> {
      const blob = await loadDocument(documentId)
      let raw = await blob.text()
      const ids = [...raw.matchAll(ASSET_RE)].map((match) => match[1]).filter(isString)
      const unique = [...new Set(ids)]
      for (const assetId of unique) {
        const asset = await loadAsset(documentId, assetId)
        const url = URL.createObjectURL(asset)
        urls.push(url)
        raw = raw.replaceAll(`pixelsign-asset:${assetId}`, url)
      }
      if (cancelled) {
        for (const url of urls) {
          URL.revokeObjectURL(url)
        }
        return
      }
      blobUrlsRef.current = urls
      setHtml(raw)
    }
    void load()
    return () => {
      cancelled = true
      for (const url of blobUrlsRef.current) {
        URL.revokeObjectURL(url)
      }
      blobUrlsRef.current = []
    }
  }, [documentId, loadDocument, loadAsset])

  React.useLayoutEffect(() => {
    const host = hostRef.current
    if (!host || html == null) {
      return
    }
    const next = new Map<string, Element>()
    host.querySelectorAll('[data-ps-field]').forEach((node) => {
      const name = node.getAttribute('data-ps-field')
      if (name) {
        next.set(name, node)
      }
    })
    setMounts(next)
  }, [html])

  if (html == null) {
    return (
      <div className="flex h-48 items-center justify-center">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    )
  }

  const pageThreads = questions?.threads.filter((thread) => thread.documentId === documentId) ?? []
  const onAskPin = questions?.onAskPin

  return (
    <div className={paper ? `relative ${paper}` : 'relative'}>
      <HtmlDocumentHost
        html={html}
        hostRef={hostRef}
        onAsk={
          onAskPin
            ? (event) => {
                if (!hostRef.current) {
                  return
                }
                const target = event.target
                if (!(target instanceof Element)) {
                  return
                }
                if (target.closest('[data-ps-field], button, a, input, select, textarea, label')) {
                  return
                }
                const rect = hostRef.current.getBoundingClientRect()
                if (rect.width === 0 || rect.height === 0) {
                  return
                }
                const x = ((event.clientX - rect.left) / rect.width) * 100
                const y = ((event.clientY - rect.top) / rect.height) * 100
                const quote =
                  (target.textContent ?? '').replace(/\s+/g, ' ').trim().slice(0, 80) || null
                onAskPin({ documentId, page: 1, x, y, quote })
              }
            : undefined
        }
      />
      {watermark ? (
        <div aria-hidden="true" className="sign-test-watermark sign-test-watermark-repeat absolute inset-0" />
      ) : null}
      {questions ? (
      <div className="pointer-events-none absolute inset-0">
        {pageThreads.map((thread) => (
          <div key={thread.id} className="pointer-events-auto">
            <QuestionDot
              thread={thread}
              selected={thread.id === questions.selectedThreadId}
              onSelect={questions.onSelectThread}
            />
          </div>
        ))}
        {questions.draft && questions.draft.documentId === documentId ? (
          <DraftDot x={questions.draft.x} y={questions.draft.y} />
        ) : null}
      </div>
      ) : null}
      {[...mounts.entries()].map(([name, node]) => {
        const field = fields.find((item) => item.name === name)
        if (!field) {
          return null
        }
        return createPortal(
          <FieldControl
            field={field}
            value={values[field.id]}
            viewerSignerId={viewerSignerId}
            signerSigned={signedSignerIds.includes(field.signerId)}
            onChange={(value) => onChange?.(field, value)}
            onClickSign={() => onClickSign?.(field)}
          />,
          node,
          field.id,
        )
      })}
    </div>
  )
}

/**
 * Keep the innerHTML host out of parent re-renders. React 19 compares
 * `dangerouslySetInnerHTML` by object identity and otherwise resets innerHTML,
 * which detaches the marker nodes that field portals target.
 */
const HtmlDocumentHost = React.memo(function HtmlDocumentHost({
  html,
  hostRef,
  onAsk,
}: {
  html: string
  hostRef: React.RefObject<HTMLDivElement | null>
  onAsk?: ((event: React.MouseEvent<HTMLDivElement>) => void) | undefined
}) {
  const markup = React.useMemo(() => ({ __html: html }), [html])
  return (
    <div
      ref={hostRef}
      className={`html-sign-doc overflow-auto rounded-md border border-border p-6${onAsk ? ' cursor-crosshair' : ''}`}
      dangerouslySetInnerHTML={markup}
      onClick={onAsk}
    />
  )
})

function isString(value: string | undefined): value is string {
  return typeof value === 'string'
}
